import { useState, useEffect, useMemo } from "react";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { useI18n } from "i18n";
import { selectAuth } from "modules/auth/redux/selectors";
import useCompanyEventDirectory from "modules/eventDirectory/hooks/useCompanyEventDirectory";
import EventDirectorySection from "modules/eventDirectory/components/EventDirectorySection";
import EventListToggle from "modules/event/eventList/EventListToggle";
import {
  selectNewEvent,
  selectOngoingMyEvents,
  selectUpcomingMyEvents,
} from "modules/event/selectors";
import { setNewEventId } from "modules/event/eventSlice";
import { usePopulateEventData } from "modules/event/hooks/apiHooks";
import { useEventQuery } from "modules/event/hooks/useEventQuery";
import { IEvent } from "modules/event/types";
import { CreateEventSuccess } from "modules/event/components";
import "./MyEventsContainer.scss";
import { WhiteLabelSettingsLoader } from "modules/settings/components/WhiteLabelSettingsLoader";
import {
  selectCustomDomainCompanyID,
  selectEnableAgencyWhiteLabelling,
} from "modules/settings/redux/selectors";
import { getRemoHosts } from "config";
import { selectCompany } from "modules/company/redux/selectors";
import JoinedEventContainer from "../JoinedEventsContainer/JoinedEventsContainer";
import { MyEventsHeader } from "../MyEventsHeader";
import { EventDiscovery } from "../EventDiscovery";

const MyEventContainer = () => {
  const ongoingMyEvents = useSelector(selectOngoingMyEvents);
  const upcomingMyEvents = useSelector(selectUpcomingMyEvents);
  const { isEventHelper } = useSelector(selectAuth);
  const { getCompanyEventDirectories } = useCompanyEventDirectory();
  const { getUpcomingMyEvents, getPastMyEvents, getOngoingMyEvents } =
    usePopulateEventData();
  const { company, isLoading: isCompanyLoading } = useSelector(selectCompany);
  const {
    data: pastMyEvents,
    fetchNextPage,
    hasNextPage,
    isFetching: isLoadingPastEvents,
    refetch: refetchPastEvents,
    totalCount,
  } = useEventQuery(company?.id || "");
  const [pastEvents, setPastEvents] = useState<IEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const newEvent = useSelector(selectNewEvent);
  const enableAgencyWhiteLabelling = useSelector(
    selectEnableAgencyWhiteLabelling,
  );
  const customDomainCompanyID = useSelector(selectCustomDomainCompanyID);
  const dispatch = useAppDispatch();
  const isHostnameCustomDomain = !getRemoHosts().includes(window.location.host);

  useMemo(() => {
    const events = pastMyEvents?.pages.reduce(
      (union, e) => union.concat(e),
      [],
    );

    setPastEvents(events ?? []);
  }, [pastMyEvents?.pages]);

  useEffect(() => {
    if (isCompanyLoading) {
      return;
    }

    if (company) {
      // eslint-disable-next-line promise/catch-or-return
      Promise.all([
        getOngoingMyEvents(company.id),
        getUpcomingMyEvents(company.id),
        getPastMyEvents(company.id),
        getCompanyEventDirectories(),
        // eslint-disable-next-line promise/prefer-await-to-then
      ]).finally(() => {
        setIsLoading(false);
      });
    }

    if (!company) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, isCompanyLoading]);

  const onSuccessModalClose = () => {
    dispatch(setNewEventId(null));
  };
  const { t } = useI18n(["common", "event"]);

  const noOfEvents: number = useMemo(
    () =>
      ((pastEvents && pastEvents.length) || 0) +
      ((upcomingMyEvents && upcomingMyEvents.length) || 0) +
      ((ongoingMyEvents && ongoingMyEvents.length) || 0),
    [pastEvents, upcomingMyEvents, ongoingMyEvents],
  );

  return (
    <>
      <MyEventsHeader
        isEventHelper={isEventHelper}
        isLoading={isLoading}
        noOfEvents={noOfEvents}
      />
      <EventDirectorySection
        isEventAssistant={isEventHelper}
        hasEvent={!!noOfEvents}
      />
      <EventListToggle
        title={t("common:ongoing", {
          key: ongoingMyEvents.length,
        })}
        events={ongoingMyEvents}
        editable
        refetch={refetchPastEvents}
      />
      <EventListToggle
        title={t("event:future.event", {
          key: upcomingMyEvents.length,
        })}
        events={upcomingMyEvents}
        editable
      />
      <EventListToggle
        title={t("event:past.event", {
          key: totalCount,
        })}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isLoading={isLoadingPastEvents}
        events={pastEvents}
        refetch={refetchPastEvents}
        editable
        totalCount={totalCount}
      />
      <JoinedEventContainer />
      {!isCompanyLoading &&
        !(
          enableAgencyWhiteLabelling &&
          isHostnameCustomDomain &&
          company?.id === customDomainCompanyID
        ) && <EventDiscovery />}

      {isLoading && <PageLoader message={t("wait")} />}

      {newEvent && (
        <CreateEventSuccess event={newEvent} onClose={onSuccessModalClose} />
      )}
      <WhiteLabelSettingsLoader />
    </>
  );
};

export default MyEventContainer;
